import debounce from 'lodash/debounce';

import { DEBOUNCE_DELAY, GERMANY_COUNTRY_CODE, COMPANY_FIELDS } from './addressInput.constants';

export const createDebouncedSearch = () => {
  const debouncedFn = debounce((searchFn, args) => searchFn(args), DEBOUNCE_DELAY);

  const trigger = (searchFn, value, buildArgs) => {
    if (!searchFn) return;

    const normalized = String(value ?? '').trim();
    debouncedFn.cancel();

    if (!normalized) return;
    debouncedFn(searchFn, buildArgs(normalized));
  };

  return {
    trigger,
    cancel: () => debouncedFn.cancel(),
  };
};

export const buildInitialDisplayOptions = (formikValues = {}) => ({
  postalCity:
    formikValues.companyPostal && formikValues.companyCity
      ? [
          {
            title: `${formikValues.companyPostal} - ${formikValues.companyCity}`,
            value: formikValues.companyPostalCity,
            postcode: formikValues.companyPostal,
            cityName: formikValues.companyCity,
          },
        ]
      : [],
  street: formikValues.companyStreet
    ? [
        {
          title: formikValues.companyStreet,
          value: formikValues.companyStreetId,
          streetId: formikValues.companyStreetId,
          streetName: formikValues.companyStreet,
          districtId: formikValues.companyRegionId || null,
          districtName: formikValues.companyRegion || null,
        },
      ]
    : [],
  houseNumber: formikValues.companyHouseNumber
    ? [
        {
          title: formikValues.companyHouseNumber,
          houseNumber: formikValues.companyHouseNumber,
        },
      ]
    : [],
});

export const buildInitialDistrictOptions = (formikValues = {}) => {
  const { companyRegion, companyRegionId } = formikValues;
  if (!companyRegion || !companyRegionId) return [];
  return [{ title: companyRegion, value: companyRegionId }];
};

/**
 * Keeps the current Formik street selection in the options list while search results
 * are partial, so the Autocomplete does not go blank until the debounced query catches up.
 */
export const mergeStreetSearchOptionsWithSelection = (searchOptions = [], formikValues = {}) => {
  const fromForm = buildInitialDisplayOptions(formikValues).street;
  const search = searchOptions ?? [];
  const streetId = formikValues?.companyStreetId;
  const hasCommittedSelection = fromForm.length > 0 && String(streetId ?? '').trim() !== '';

  if (!hasCommittedSelection) {
    return search.length ? search : [];
  }

  const [current] = fromForm;
  const inSearch = search.some((o) => String(o.value) === String(current.value));
  if (inSearch) {
    return search.length ? search : fromForm;
  }
  if (!search.length) {
    return fromForm;
  }
  return [current, ...search];
};

export const hasSelectedCountry = (formikValues = {}) => {
  const countryFieldExists = 'companyCountry' in formikValues;
  return (
    !countryFieldExists || Boolean(String(formikValues.companyCountry ?? '').trim())
  );
};

export const getFormikFieldProps = ({ formik, name, isComboBox, showErrorsWhenTouched }) => {
  if (!formik || !name) return {};

  const isGermanAddress =
    formik.values?.companyCountry === undefined ||
    formik.values?.companyCountry === GERMANY_COUNTRY_CODE;
  const lokarefRequiredFields = [
    COMPANY_FIELDS.POSTAL_CITY_ID,
    COMPANY_FIELDS.STREET_ID,
    COMPANY_FIELDS.HOUSE_NUMBER,
  ];
  const showError = showErrorsWhenTouched
    ? Boolean(
        formik.errors?.[name] && (formik.touched?.[name] || formik.submitCount > 0)
      )
    : (formik.errors?.[name] && formik.touched?.[name]) ||
      (isGermanAddress && lokarefRequiredFields.includes(name) && !formik.values?.[name]);

  return {
    name,
    value: formik.values?.[name] ?? '',
    error: Boolean(showError),
    helperText: showError ? formik.errors?.[name] || 'Pflichtfeld' : '',
    onChange: isComboBox ? formik.setFieldValue : formik.handleChange,
    onBlur: formik.handleBlur,
  };
};
